import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, catchError, finalize, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { LoaderService } from './Shared/loader/loader.service';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { SKIP_AUTH_REFRESH, UserLoginService } from './Common/UserLoginService/userlogin.service';

// Per-request opt-in, not a global setting: set via `{ context: new
// HttpContext().set(FORCE_PROVIDER_TENANT_HEADERS, true) }` on an
// individual request's options. Below, CompanyId/BranchId normally come
// from localStorage (the logged-in user's own tenant) unconditionally via
// setHeaders, which would otherwise silently overwrite any header a caller
// tries to set itself. The support chat widget needs CompanyId/BranchId
// pinned to the Provider tenant (1/1) on its SPD calls regardless of who's
// logged in — CompanyConfigMiddleware resolves the DB connection from these
// headers, and SupportDesk's tables live only in the Provider's own
// database, not each client's — while every other caller (including the
// admin's own SupportDesk pages) must keep getting the normal per-user
// headers, so this is scoped per-request rather than changed here globally.
export const FORCE_PROVIDER_TENANT_HEADERS = new HttpContextToken<boolean>(() => false);

// Per-request opt-in, paired with SKIP_TENANT_URL_REWRITE (see
// api-url-rewrite.interceptor.ts): pages that read a VIEWED tenant's data
// from that tenant's own server (e.g. Customer Details' apiConfigOverride
// reads) also need CompanyId/BranchId to match that viewed tenant, not the
// logged-in admin's own tenant from localStorage - otherwise the request
// lands on the right server with the wrong tenant's headers, and
// CompanyConfigMiddleware there 404s with "Database Configuration Not
// Found" for a CompanyId that server doesn't host. Set via
// `{ context: new HttpContext().set(TENANT_HEADER_OVERRIDE, { companyId, branchId }) }`.
export interface TenantHeaderOverride {
  companyId: string | number;
  branchId: string | number;
}
export const TENANT_HEADER_OVERRIDE = new HttpContextToken<TenantHeaderOverride | null>(() => null);

@Injectable()
export class ValidationInterceptor implements HttpInterceptor {

  constructor(private router: Router, private loaderService: LoaderService, private toaster: ToastrService, private authService: UserLoginService) { }

  trimObjectStrings(obj: any): any {
    if (obj === null || obj === undefined) return obj;

    if (typeof obj === 'string') {
      return obj.trim();
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.trimObjectStrings(item));
    }

    if (typeof obj === 'object') {
      const trimmedObj: any = {};
      Object.keys(obj).forEach(key => {
        trimmedObj[key] = this.trimObjectStrings(obj[key]);
      });
      return trimmedObj;
    }

    return obj; // numbers, booleans, etc.
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

    const originalRequest = request;
    const forceProviderTenant = request.context.get(FORCE_PROVIDER_TENANT_HEADERS);
    const tenantHeaderOverride = request.context.get(TENANT_HEADER_OVERRIDE);

    request = request.clone({
      setHeaders: {
        'ClientId': environment.appClientId,
        'CompanyId': forceProviderTenant
          ? String(environment.supportProviderTenantId)
          : tenantHeaderOverride
            ? String(tenantHeaderOverride.companyId)
            : (localStorage.getItem('CompanyId') ?? environment.CompanyId),
        'BranchId': forceProviderTenant
          ? String(environment.supportProviderTenantBranchId)
          : tenantHeaderOverride
            ? String(tenantHeaderOverride.branchId)
            : (localStorage.getItem('BranchId') ?? environment.BranchId),
        'SoftwareId': String(environment.SoftwareId)
      }
    })

    const token = localStorage.getItem('access_token');
    if (token) {
      request = request.clone({
        setHeaders: {
          'Authorization': `Bearer ${token}`,
        }
      })
    }

    
    // 👉 Trim the body (if JSON)
    // let body = request.body;
    // if (body && typeof body === 'object') {
    //   body = this.trimObjectStrings(body);
    // }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Expired access token (gateway 401, or the service's own 400 "Token Expired"): silently
        // swap the refresh token for a new access token and replay this request once. Only when a
        // refresh token exists and this request isn't itself the refresh/logout call or a replay.
        const tokenExpired = error.status === 401 || (error.status === 400 && error.error === 'Token Expired');
        if (tokenExpired && token && localStorage.getItem('refresh_token') && !request.context.get(SKIP_AUTH_REFRESH)) {
          return this.authService.refreshAccessToken(token).pipe(
            catchError((refreshError) => {
              // The server rejecting the refresh token (401) already signed the user out in the
              // branch below; offline/5xx keep the session so the user can retry. Only a refresh
              // that produced no usable token (an Error, not an HTTP error string) is handled here.
              if (refreshError instanceof Error) {
                this.toaster.error('Session Expired! Please log in again.');
                this.authService.SignOutUser();
              }
              return throwError(() => `Error Code: ${error.status},  Message: ${error.message}`);
            }),
            switchMap(() => this.intercept(
              originalRequest.clone({ context: originalRequest.context.set(SKIP_AUTH_REFRESH, true) }), next))
          );
        }

        let errorMsg = '';
        console.log(request);
        if (error.error instanceof ErrorEvent) {
          console.log('This is CLIENT side error');
          errorMsg = `Error: ${error.error.message}`;
        } else {
          console.log('This is SERVER side error');
          errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;

          if (error.status == 401) {
            // Only a request that carried the CURRENT session's token can expire
            // it: no token = already signed out (e.g. a 401 from the login page
            // itself, or a late response from the old session) and must not
            // wipe the tenant config the login page just loaded. SignOutUser()
            // is idempotent, so concurrent 401s sign out exactly once.
            if (token && request.headers.get('Authorization') === `Bearer ${localStorage.getItem('access_token')}`) {
              this.toaster.error('Session Expired! Please log in again.');
              this.authService.SignOutUser();
            }
          } else if (request.url.includes('/UL/Refresh') || request.url.includes('/UL/Logout')) {
            // Best-effort auth calls: failures (offline, 5xx) are silent.
            console.warn('Auth refresh/logout call failed:', error.status);
          } else if (error.statusText === 'Tenant configuration unavailable' && !localStorage.getItem('access_token')) {
            // Stale in-flight calls from the expired session hit the interceptor
            // while the login page is still loading its config - expected and
            // transient, and the login page shows its own setup error if the
            // config really fails. Don't toast it.
            console.warn('Skipped request - tenant configuration not loaded yet:', request.url);
          } else {
            console.log(error.error);

            if (error.error != 'Token Expired') {

              if ((error.error['Message'] && error.error['Message'].length > 0) || (error.error['message'] && error.error['message'].length > 0)) {
                this.toaster.info(error.error['Message'] ?? error.error['message'] ?? '');

              } else {

                this.toaster.error(error.error);
              }

            }
          }
        }
        return throwError(() => errorMsg);
      }),
      finalize(() => {

      })
    )
  }
}